// ErrorFallback.jsx - A standalone component that includes the feedback form
import BugReportIcon from "@mui/icons-material/BugReport";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FeedbackIcon from "@mui/icons-material/Feedback";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

// Error fallback component with inline feedback form
const ErrorFallback = ({ error, resetErrorBoundary, submitFeedback }) => {
  const [formVisible, setFormVisible] = useState(true);
  const [title, setTitle] = useState("Error Report");
  const [details, setDetails] = useState("");
  const [userAction, setUserAction] = useState("");
  const [errorTrace, setErrorTrace] = useState(
    error.stack ? `${error.message}\n\n${error.stack}` : error.message,
  );
  const [feedbackType, setFeedbackType] = useState("bug");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Helper function to collect browser information
  const collectBrowserInfo = () => {
    try {
      return {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        cookiesEnabled: navigator.cookieEnabled,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        url: window.location.href,
        pathname: window.location.pathname,
      };
    } catch (e) {
      return { error: "Could not collect browser info" };
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const feedbackData = {
        title,
        feedback_type: feedbackType,
        details,
        user_action: userAction,
        error_trace: errorTrace,
        browser_info: collectBrowserInfo(),
      };

      if (submitFeedback) {
        await submitFeedback(feedbackData);
        setSubmitSuccess(true);
        // Reset form after successful submission
        setTimeout(() => {
          setFormVisible(false);
          setSubmitSuccess(false);
        }, 3000);
      } else {
        console.log("Feedback would be submitted:", feedbackData);
        setSubmitSuccess(true);
        setTimeout(() => {
          setFormVisible(false);
          setSubmitSuccess(false);
        }, 3000);
      }
    } catch (e) {
      console.error("Error submitting feedback:", e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        maxWidth: "800px",
        mx: "auto",
        my: 4,
      }}
    >
      {/* Error information card */}
      <Card
        elevation={3}
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 2,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "4px",
            bgcolor: "error.main",
          },
        }}
      >
        <CardContent>
          <Stack direction="column" alignItems="center" spacing={2}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
            <Typography
              variant="h5"
              color="error.dark"
              fontWeight="bold"
              gutterBottom
            >
              Something went wrong!
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: "500px", textAlign: "center" }}
            >
              {error.message}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={resetErrorBoundary}
                startIcon={<RefreshIcon />}
                disabled={isSubmitting}
              >
                Try again
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setFormVisible(!formVisible)}
                startIcon={<FeedbackIcon />}
                disabled={isSubmitting || submitSuccess}
              >
                {formVisible ? "Hide feedback form" : "Report issue"}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Feedback Form Card - Only shown when requested */}
      {formVisible && (
        <Card
          elevation={2}
          sx={{
            mt: 3,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CardContent>
            <Stack spacing={3}>
              <Typography
                variant="h6"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <FeedbackIcon color="primary" sx={{ mr: 1 }} />
                Report Issue
              </Typography>

              <Divider />

              <FormControl component="fieldset">
                <FormLabel component="legend">Feedback Type</FormLabel>
                <RadioGroup
                  row
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                >
                  <FormControlLabel
                    value="bug"
                    control={<Radio />}
                    label="Bug Report"
                  />
                  <FormControlLabel
                    value="feature"
                    control={<Radio />}
                    label="Feature Request"
                  />
                  <FormControlLabel
                    value="improvement"
                    control={<Radio />}
                    label="Improvement"
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio />}
                    label="Other"
                  />
                </RadioGroup>
              </FormControl>

              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                variant="outlined"
              />

              <TextField
                label="What were you doing when the issue occurred?"
                multiline
                rows={2}
                value={userAction}
                onChange={(e) => setUserAction(e.target.value)}
                fullWidth
                variant="outlined"
                placeholder="Please describe the steps you were taking when you encountered this issue..."
              />

              <TextField
                label="Details of the issue"
                multiline
                rows={3}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                fullWidth
                variant="outlined"
                placeholder="Please provide any additional details about the issue..."
              />

              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <BugReportIcon fontSize="small" color="error" />
                  <Typography variant="body2" color="text.secondary">
                    Error Information (automatically captured)
                  </Typography>
                </Stack>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  variant="outlined"
                  value={errorTrace}
                  onChange={(e) => setErrorTrace(e.target.value)}
                  InputProps={{
                    sx: {
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      bgcolor: "rgba(0, 0, 0, 0.03)",
                    },
                  }}
                />
              </Box>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => setFormVisible(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting || submitSuccess || !title.trim()}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : submitSuccess
                      ? "Submitted!"
                      : "Submit Feedback"}
                </Button>
              </Stack>

              {submitSuccess && (
                <Typography color="success.main" textAlign="center">
                  Thank you for your feedback! It has been submitted
                  successfully.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ErrorFallback;
